import { useCallback } from 'react';

import { useAppDispatch, useAppSelector } from '@/app/store/hooks';

import {
  useBackofficeLoginMutation,
  useBackofficeLogoutMutation,
} from '../api/backoffice-auth-api';
import {
  clearBackofficeSession,
  selectBackofficeAccessToken,
  selectBackofficeUser,
  selectIsBackofficeAuthenticated,
  setBackofficeSession,
} from '../store/backoffice-auth-slice';

interface BackofficeLoginCredentials {
  email: string;
  password: string;
  rememberMe?: boolean;
}

export function useBackofficeAuth() {
  const dispatch = useAppDispatch();
  const accessToken = useAppSelector(selectBackofficeAccessToken);
  const user = useAppSelector(selectBackofficeUser);
  const isAuthenticated = useAppSelector(selectIsBackofficeAuthenticated);
  const [loginMutation, loginState] = useBackofficeLoginMutation();
  const [logoutMutation, logoutState] = useBackofficeLogoutMutation();

  const login = useCallback(
    async ({
      email,
      password,
      rememberMe = true,
    }: BackofficeLoginCredentials) => {
      const result = await loginMutation({ email, password }).unwrap();
      dispatch(
        setBackofficeSession({
          accessToken: result.accessToken,
          user: result.user,
          rememberMe,
        }),
      );
      return result;
    },
    [dispatch, loginMutation],
  );

  const logout = useCallback(async () => {
    try {
      await logoutMutation(undefined).unwrap();
    } catch {
      // Always clear local session even if the API call fails.
    } finally {
      dispatch(clearBackofficeSession());
    }
  }, [dispatch, logoutMutation]);

  const clearSession = useCallback(() => {
    dispatch(clearBackofficeSession());
  }, [dispatch]);

  return {
    accessToken,
    user,
    isAuthenticated,
    login,
    logout,
    clearSession,
    isLoggingIn: loginState.isLoading,
    isLoggingOut: logoutState.isLoading,
  };
}
